import { useEffect, useState } from "react";

import { Button, Icon, Spinner, Tag } from "@blueprintjs/core";
import { useSearchParams } from "react-router";

import onboardGraphic from "@/../../hypetrain/static/img/coach-onboard.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, BoxProps, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { CARD_CLASSES } from "@/css/constants";
import {
  CoachPayoutAccountDocument,
  useAccountLinkMutation,
  useUpdateStripeAccountMutation,
} from "@/graphql";
import { CoachingUser } from "@/types/users";

function StripeOnboarding() {
  const [searchParams] = useSearchParams();
  const [createAccountLink, { data: accountLinkData }] = useAccountLinkMutation(
    {
      onCompleted: (response) => {
        if (response.accountLink?.url) {
          window.location.href = response.accountLink.url;
        }
      },
    },
  );

  const [updateAccount, { data: updatedData, loading: updating }] =
    useUpdateStripeAccountMutation({
      refetchQueries: [{ query: CoachPayoutAccountDocument }],
    });

  function handleRedirect() {
    setRedirecting(true);

    // Create new URLSearchParams with current params
    const newParams = new URLSearchParams(searchParams);
    newParams.set("stripeComplete", "1");

    createAccountLink({
      variables: {
        returnUrl: `${window.location.origin}${window.location.pathname}?${newParams.toString()}`,
      },
    });
  }

  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!searchParams.has("stripeComplete")) {
      return;
    }
    updateAccount();
  }, []);

  useEffect(() => {
    if (!redirecting || !accountLinkData) return;
    const timer = setTimeout(() => setRedirecting(false), 1000);
    return () => clearTimeout(timer);
  }, [accountLinkData]);

  useEffect(() => {
    if (
      updating ||
      !updatedData ||
      updatedData?.updateStripeAccount?.ready ||
      !updatedData?.updateStripeAccount?.submitted
    )
      return;

    const timer = setTimeout(() => updateAccount(), 10000);
    return () => clearTimeout(timer);
  }, [updating]);

  if (updating) {
    return (
      <Flex alignItems="center" mt={2}>
        <Spinner size={16} />
        <Box ml={2}>Checking status</Box>
      </Flex>
    );
  }

  if (updatedData) {
    if (updatedData.updateStripeAccount?.ready) {
      return (
        <Flex alignItems="center" mt={2}>
          <Icon icon="tick" intent="success" size={16} />
          <Box ml={2}>You're all set!</Box>
        </Flex>
      );
    } else if (updatedData?.updateStripeAccount?.submitted) {
      return (
        <Flex alignItems="center" mt={2}>
          <Spinner size={16} />
          <Box ml={2}>
            Awaiting verification. This could take up to 2 minutes.
          </Box>
        </Flex>
      );
    }
  }

  return (
    <>
      <Box mt={2} maxWidth={380}>
        Please complete our quick payment onboarding process in order to get
        paid
      </Box>
      <Box mt={24} width={["100%", "100%", "100%", 275]}>
        <Button
          large={true}
          intent="primary"
          fill={true}
          onClick={handleRedirect}
          loading={redirecting}
          disabled={redirecting}
        >
          Get Started
        </Button>
      </Box>
    </>
  );
}

function GustoOnboarding() {
  return (
    <>
      <Box mt={2} maxWidth={380}>
        Please check your email for a message from Gusto to complete your
        payment onboarding.
      </Box>
    </>
  );
}

interface CoachOnboardingProps extends BoxProps {
  user: CoachingUser;
}

export function CoachOnboarding({ user, ...props }: CoachOnboardingProps) {
  if (!user.agentProfile || user.agentProfile.paymentOnboardingReady) {
    return null;
  }

  return (
    <Flex
      className={CARD_CLASSES}
      backgroundColor="#FFF"
      justifyContent="space-between"
      alignItems="center"
      p={[24, "16px 16px 16px 24px"]}
      {...props}
    >
      <Box>
        <Tag intent="warning">
          <Box fontWeight={600} fontSize={12} letterSpacing={0.5}>
            ACTION REQUIRED
          </Box>
        </Tag>
        <Box fontWeight={600} fontSize={20} mt={2}>
          Activate Payouts
        </Box>
        {user.agentProfile?.payoutChannel === "STRIPE_CONNECT" ? (
          <StripeOnboarding />
        ) : (
          <GustoOnboarding />
        )}
      </Box>
      <Media greaterThan="xs">
        <EmbedSvg src={onboardGraphic} height={200} />
      </Media>
    </Flex>
  );
}
